import anime from 'animejs';
import Image from 'next/image';
import Link from 'next/link';
import type React from 'react';
import { type HTMLAttributes, useEffect } from 'react';

import cn from '@/helper/cn';
import type { ConsentOptions, ConsentParams } from '@/lib/cookies';

const COOKIE_STORAGE_KEY = 'mindsite-cookies';

type Props = {
  consent: (arg: ConsentOptions) => void;
};

const CookiePopupButton = ({
  children,
  id,
  onClick,
  className,
  ...props
}: HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      id={id}
      className={cn(
        'rounded-full bg-brand-600 px-[14px] py-2 text-white transition-colors duration-200 hover:bg-opacity-80 focus:bg-opacity-80 active:bg-opacity-80',
        className,
      )}
      onClick={onClick}
      type="button"
      {...props}
    >
      {children}
    </button>
  );
};

const CookiePopup = ({ consent }: Props) => {
  // Check if user has already accepted cookies
  const shouldCookieShowUp = !localStorage.getItem(COOKIE_STORAGE_KEY);

  // Popup trigger animation
  useEffect(() => {
    const isScreenMd = window.matchMedia('(min-width:768px)').matches;

    anime({
      targets: '#cookie-popup',
      opacity: [0, 1],
      translateX: isScreenMd ? [100, 0] : undefined,
      translateY: isScreenMd ? undefined : [100, 0],
      duration: 1000,
      easing: 'easeOutExpo',
    });
  }, []);

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    const isScreenMd = window.matchMedia('(min-width:768px)').matches;
    let permissions: ConsentParams = {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
      wait_for_update: 500, // Update delay
    };

    // NOTE: In case of further more options (e.g "necessary-only")
    switch ((e.target as HTMLButtonElement).id) {
      case 'accept-all':
        break;
      case 'decline':
        permissions = {
          ...permissions,
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          analytics_storage: 'denied',
        };
        break;
      default:
        break;
    }

    anime({
      targets: '#cookie-popup',
      opacity: [1, 0],
      translateX: isScreenMd ? [0, 100] : undefined,
      translateY: isScreenMd ? undefined : [0, 100],
      duration: 1000,
      easing: 'easeOutExpo',
      complete: () => {
        localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify(permissions));
      },
    });

    consent({ arg: 'update', params: permissions });
  };

  if (!shouldCookieShowUp) {
    return <></>;
  }

  return (
    <div
      id="cookie-popup"
      className="fixed bottom-0 right-0 flex w-full flex-col items-center gap-5 bg-white px-3 py-5 text-[#212427] sm:flex-row sm:justify-end md:bottom-8 md:w-[520px] md:rounded-l-xl"
    >
      <div className="mx-auto w-fit sm:mx-[unset] sm:w-full sm:max-w-fit">
        <Image
          src="/cookie.png"
          alt="Cookie"
          quality={100}
          priority
          width={110}
          height={70}
        />
      </div>
      <div className="flex flex-col gap-4 text-sm sm:text-clip">
        <div className="text-center text-xs font-medium sm:text-left">
          <p>
            <strong>We use cookies</strong> on our website, and when you
            continue to use our site, your personal data collected through
            cookies will be processed as described in the{' '}
            <Link
              href="/docs/cookie-notice.pdf"
              target="_blank"
              className="underline transition-colors duration-200 hover:text-brand-600"
            >
              Cookie Notice.
            </Link>
          </p>
        </div>
        <div className="flex justify-center gap-4 font-medium sm:justify-end md:justify-start">
          <CookiePopupButton id="accept-all" onClick={handleSubmit}>
            Accept all
          </CookiePopupButton>
          {/* <CookiePopupButton
              id="necessary-only"
              className="bg-brand-300"
              onClick={handleSubmit}
            >
              Necessary
            </CookiePopupButton> */}
          <CookiePopupButton
            id="decline"
            onClick={handleSubmit}
            className="border border-[#CDD0D1] bg-transparent text-[#7B8186] hover:border-zinc-400 hover:text-zinc-700 focus:border-zinc-400 focus:text-zinc-700 active:border-zinc-400 active:text-zinc-700"
          >
            Decline all
          </CookiePopupButton>
        </div>
      </div>
    </div>
  );
};

export default CookiePopup;
